import classNames from 'classnames';
import isArray from 'lodash-es/isArray';
import omit from 'lodash-es/omit';
import { FormattedMessage } from 'react-intl';

import { useDataContext } from '../../contexts/DataContext';
import BasicButton from '../buttons/BasicButton';
import SelectField from '../fields/SelectField';

import styles from '../../styles/blocks/data-filters-block.module.css';

interface DataFiltersBlockProps {
    dimensions?: string[];
    withCount?: boolean;
    className?: string;
}

function DataFiltersBlock({ dimensions, withCount = true, className }: DataFiltersBlockProps) {
    const { data, filters, setFilters } = useDataContext();
    const onFieldChange = (field: string, value: unknown) => {
        if (value === null || value.length === 0) {
            setFilters(omit(filters, [field]));
        } else {
            setFilters({
                ...filters,
                [field]: value,
            });
        }
    };
    const fields = (dimensions || []).map((dimension) => ({
        name: dimension,
        options: [
            {
                label: dimension,
                value: '',
            },
            {
                label: '--',
                value: '',
            },
            ...(data || [])
                .reduce((values, item) => {
                    const value =
                        item[dimension] ||
                        item[dimension.replace(/’/, "'")] ||
                        item[dimension.replace(/'/, '’')] ||
                        null;

                    if (value === null) {
                        return values;
                    }
                    const newValues = isArray(value) ? value : [value];
                    return [...values, ...newValues.filter((v) => values.indexOf(v) === -1)];
                }, [])
                .map((value) => ({ label: value, value }))
                .sort(({ label: a }, { label: b }) => (a < b ? -1 : a > b ? 1 : 0)),
        ],
    }));

    const filteredData = (data || []).filter((it) => {
        if (filters === null || Object.keys(filters).length === 0) {
            return true;
        }
        return Object.keys(filters).reduce(
            (all, dim) =>
                all &&
                ((isArray(it[dim]) && it[dim].indexOf(filters[dim]) !== -1) ||
                    it[dim] === filters[dim]),
            true,
        );
    });
    const count = (filteredData || []).length;

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.withFilters]: filters !== null && Object.keys(filters).length > 0,
                },
                className,
                'drag-disabled',
            ])}
        >
            <div className={styles.inner}>
                <div className={styles.fields}>
                    {fields.map(({ name, options }) => (
                        <SelectField
                            key={`field-${name}`}
                            placeholder={name}
                            options={options}
                            value={filters !== null ? filters[name] || null : null}
                            onChange={(newValue) => onFieldChange(name, newValue)}
                            className={styles.field}
                        />
                    ))}
                </div>
                <div className={styles.toolbar}>
                    {withCount ? (
                        <div className={styles.count}>
                            <FormattedMessage
                                defaultMessage="{count, plural,
  =0 {Aucun résultat}
  one {# résultat}
  other {# résultats}
}"
                                description="Data filters count"
                                values={{ count }}
                            />
                        </div>
                    ) : null}
                    <BasicButton
                        type="button"
                        className={styles.clearButton}
                        onClick={() => setFilters(null)}
                    >
                        <span className={styles.label}>Effacer les filtres</span>
                        <span className={classNames([styles.icon, 'material-symbols-rounded'])}>
                            close
                        </span>
                    </BasicButton>
                </div>
            </div>
        </div>
    );
}

export default DataFiltersBlock;
